import React, { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Bell,
  Brain,
  ChevronRight,
  CircleDot,
  Cpu,
  FlaskConical,
  Gauge,
  Hexagon,
  Lock,
  Pause,
  Phone,
  Play,
  Power,
  Radio,
  RefreshCw,
  ScanLine,
  Send,
  Shield,
  Sparkles,
  Terminal,
  TrendingUp,
  Waves,
  Zap,
} from "lucide-react";
import { buildAeonHubState, hubTabs } from "./aeonHubData";

const moduleMeta = {
  hub: {
    label: "Hub",
    icon: Hexagon,
    signal: "core",
    title: "AEON Hub",
    brief: "Central operating layer for private market command.",
  },
  engines: {
    label: "Engines",
    icon: Cpu,
    signal: "logic",
    title: "Engine Bay",
    brief: "Strategy units, edge pressure, tier health, and governance states.",
  },
  exposure: {
    label: "Exposure",
    icon: Gauge,
    signal: "heat",
    title: "Exposure Field",
    brief: "Portfolio heat, account pressure, directional balance, and concentration.",
  },
  oracle: {
    label: "Oracle",
    icon: Brain,
    signal: "sense",
    title: "Oracle Layer",
    brief: "Regime interpretation, entropy, confidence, and tactical blindspots.",
  },
  control: {
    label: "Control",
    icon: Shield,
    signal: "gates",
    title: "Control Room",
    brief: "Operator actions, trust gates, alerts, voice link, and hard stops.",
  },
  researcher: {
    label: "Lab",
    icon: FlaskConical,
    signal: "research",
    title: "Quantum Lab",
    brief: "Hurst regime gate, ensemble consensus, TCN votes, calibration analysis.",
  },
};

const toneForRisk = (risk) => {
  if (String(risk).includes("elevated")) return "danger";
  if (String(risk).includes("guarded")) return "warning";
  return "live";
};

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function ModuleDock({ active, setActive, data }) {
  const heat = Math.min(100, Math.max(0, Number(data.exposure.portfolioHeat || 0)));

  return (
    <nav className="aeon-os-dock" aria-label="AEON Hub modules">
      {hubTabs.map((tab) => {
        const meta = moduleMeta[tab];
        const Icon = meta.icon;
        const selected = active === tab;
        return (
          <button key={tab} onClick={() => setActive(tab)} className={selected ? "is-active" : ""}>
            <span className="aeon-os-dock-icon"><Icon className="h-5 w-5" /></span>
            <span className="aeon-os-dock-text">
              <strong>{meta.label}</strong>
              <small>{tab === "exposure" ? `${heat}% ${meta.signal}` : meta.signal}</small>
            </span>
            <span className="aeon-os-dock-pulse" />
          </button>
        );
      })}
    </nav>
  );
}

function OrbitalCore({ data, active, setActive }) {
  const { system, exposure } = data;
  const health = Math.max(0, Math.min(100, Number(system.healthScore || 0)));
  const riskTone = toneForRisk(system.riskState);
  const liveRatio = Math.max(0.08, Math.min(1, Number(system.activeEngines || 0) / Math.max(1, Number(system.totalEngines || 1))));
  const heatRatio = Math.max(0.08, Math.min(1, Number(exposure.portfolioHeat || 0) / 100));

  return (
    <section className={`aeon-os-core aeon-os-core-${riskTone}`}>
      <div className="aeon-os-core-grid" />
      <div className="aeon-os-radar-sweep" />
      <div className="aeon-os-orbit aeon-os-orbit-a" />
      <div className="aeon-os-orbit aeon-os-orbit-b" />
      <div className="aeon-os-orbit aeon-os-orbit-c" />
      <button className="aeon-os-core-orb" onClick={() => setActive("hub")} aria-label="Return to Hub core">
        <span className="aeon-os-orb-glass" />
        <span className="aeon-os-orb-title">ct</span>
        <strong>{health}</strong>
        <small>HUB CORE</small>
      </button>
      <div className="aeon-os-vector aeon-os-vector-engines" style={{ "--load": liveRatio }} />
      <div className="aeon-os-vector aeon-os-vector-heat" style={{ "--load": heatRatio }} />
      {["engines", "exposure", "oracle", "control"].map((tab, index) => {
        const Icon = moduleMeta[tab].icon;
        return (
          <button
            key={tab}
            onClick={() => setActive(tab)}
            className={`aeon-os-satellite aeon-os-satellite-${index + 1} ${active === tab ? "is-active" : ""}`}
          >
            <Icon className="h-4 w-4" />
            <span>{moduleMeta[tab].label}</span>
          </button>
        );
      })}
    </section>
  );
}

function StatusStrip({ props, data }) {
  const { system, exposure } = data;
  return (
    <div className="aeon-os-status-strip">
      <div><span>Stream</span><strong className={props.wsConnected ? "is-live" : "is-warning"}>{props.wsConnected ? "LIVE" : "RELINK"}</strong></div>
      <div><span>Mode</span><strong className={props.tradingStats?.active ? "is-live" : "is-warning"}>{props.tradingStats?.active ? "ACTIVE" : "PAUSED"}</strong></div>
      <div><span>Risk</span><strong className={`is-${toneForRisk(system.riskState)}`}>{system.riskState}</strong></div>
      <div><span>Heat</span><strong>{exposure.portfolioHeat}%</strong></div>
      <div><span>Regime</span><strong>{system.regime}</strong></div>
    </div>
  );
}

function DispatchFeed({ feed, warnings = [], banner, unreadAlerts, onClear }) {
  const packets = [
    banner ? { id: "banner", tone: "danger", title: "REGIME SHIFT", body: `${banner.from} to ${banner.to}${banner.H ? ` / H ${banner.H}` : ""}` } : null,
    ...warnings.map((warning, index) => ({ id: `warning-${index}`, tone: "warning", title: "OPERATOR WARNING", body: warning })),
    ...feed,
  ].filter(Boolean).slice(0, 7);

  return (
    <section className="aeon-os-dispatch">
      <div className="aeon-os-section-head">
        <div>
          <span>Dispatch</span>
          <h2>Live command feed</h2>
        </div>
        {unreadAlerts ? <button onClick={onClear}>{unreadAlerts} clear</button> : <Activity className="h-5 w-5" />}
      </div>
      <div className="aeon-os-feed-line" />
      <div className="aeon-os-feed">
        {packets.length ? packets.map((item, index) => (
          <article key={item.id || index} className={`aeon-os-packet aeon-os-packet-${item.tone || "live"}`}>
            <CircleDot className="h-4 w-4" />
            <div>
              <strong>{item.title || "dispatch"}</strong>
              <p>{item.body || "Signal packet received."}</p>
            </div>
          </article>
        )) : (
          <article className="aeon-os-packet aeon-os-packet-live">
            <CircleDot className="h-4 w-4" />
            <div><strong>STANDBY</strong><p>No pressure packets in the current window.</p></div>
          </article>
        )}
      </div>
    </section>
  );
}

function StatBlade({ label, value, detail, tone = "live", icon: Icon = Zap }) {
  return (
    <div className={`aeon-os-blade aeon-os-blade-${tone}`}>
      <Icon className="h-5 w-5" />
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function HubScreen({ data, props, setActive }) {
  const { system, exposure } = data;
  return (
    <div className="aeon-os-layout aeon-os-layout-hub">
      <OrbitalCore data={data} active="hub" setActive={setActive} />
      <div className="aeon-os-command-bank">
        <StatBlade icon={Cpu} label="Engine Load" value={`${system.activeEngines}/${system.totalEngines}`} detail="active strategy units" />
        <StatBlade icon={Gauge} label="Exposure" value={`${exposure.portfolioHeat}%`} detail={`${exposure.openCount} open positions`} tone={exposure.portfolioHeat > 65 ? "danger" : "warning"} />
        <StatBlade icon={Brain} label="Oracle" value={system.regime} detail="current regime read" tone="sense" />
        <StatBlade icon={Radio} label="Market" value={system.btcPrice ? formatMoney(system.btcPrice) : "SYNC"} detail="BTC reference feed" tone="signal" />
      </div>
      <div className="aeon-os-module-gates">
        {["engines", "exposure", "oracle", "control"].map((tab) => {
          const meta = moduleMeta[tab];
          const Icon = meta.icon;
          return (
            <button key={tab} onClick={() => setActive(tab)}>
              <Icon className="h-5 w-5" />
              <strong>{meta.label}</strong>
              <span>{meta.brief}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          );
        })}
      </div>
      <DispatchFeed feed={data.feed} warnings={props.operatorWarnings} banner={props.regimeBanner} unreadAlerts={props.unreadAlerts} onClear={props.actions?.clearAlerts} />
    </div>
  );
}

function EnginesScreen({ data }) {
  return (
    <div className="aeon-os-layout">
      <section className="aeon-os-engine-bay">
        {data.engines.map((engine, index) => (
          <article key={engine.name} className={`aeon-os-engine-unit aeon-os-engine-${engine.status}`}>
            <div className="aeon-os-engine-index">{String(index + 1).padStart(2, "0")}</div>
            <div>
              <strong>{engine.name}</strong>
              <span>{engine.status} / tier {engine.tier}</span>
            </div>
            <div className="aeon-os-engine-edge">{engine.edge}</div>
            <div className="aeon-os-meter"><i style={{ width: `${Math.max(8, Math.min(100, engine.heat))}%` }} /></div>
          </article>
        ))}
      </section>
      <section className="aeon-os-side-console">
        <StatBlade icon={Cpu} label="Stack" value={`${data.system.activeEngines}/${data.system.totalEngines}`} detail="live units" />
        <StatBlade icon={AlertTriangle} label="Constraint" value={data.engines.filter((engine) => ["restricted", "stale"].includes(engine.status)).length} detail="gated units" tone="danger" />
        <StatBlade icon={Sparkles} label="Edge" value={Math.round(data.engines.reduce((sum, engine) => sum + Number(engine.edge || 0), 0) / Math.max(1, data.engines.length))} detail="average signal score" tone="sense" />
      </section>
    </div>
  );
}

function ExposureScreen({ data }) {
  const { exposure } = data;
  return (
    <div className="aeon-os-layout">
      <section className="aeon-os-exposure-field">
        <div className="aeon-os-pressure-core">
          <Waves className="h-8 w-8" />
          <strong>{exposure.portfolioHeat}%</strong>
          <span>PORTFOLIO HEAT</span>
        </div>
        <div className="aeon-os-balance-line">
          <div><strong>{exposure.longPct}%</strong><span>long</span></div>
          <div><strong>{exposure.shortPct}%</strong><span>short</span></div>
        </div>
        <div className="aeon-os-symbols">
          {exposure.symbols.map((item) => (
            <div key={item.symbol}>
              <span>{item.symbol}</span>
              <i style={{ height: `${item.heat}%` }} />
              <strong>{item.heat}</strong>
            </div>
          ))}
        </div>
      </section>
      <section className="aeon-os-account-stack">
        {exposure.accounts.map((account) => {
          const heat = Math.round(Number(account.portfolio_heat || 0) * 100);
          return (
            <article key={account.account_id}>
              <div><strong>{account.account_id}</strong><span>{formatMoney(account.balance)}</span></div>
              <div className="aeon-os-meter"><i style={{ width: `${Math.max(4, heat)}%` }} /></div>
              <b>{heat}%</b>
            </article>
          );
        })}
      </section>
    </div>
  );
}

function OracleScreen({ data }) {
  const { oracle, system } = data;
  return (
    <div className="aeon-os-layout">
      <section className="aeon-os-oracle-scope">
        <div className="aeon-os-radar-disc" />
        <div className="aeon-os-oracle-read">
          <span>REGIME</span>
          <strong>{system.regime}</strong>
          <p>{oracle.volatility} volatility / {oracle.confidence}% confidence</p>
        </div>
        <div className="aeon-os-oracle-metrics">
          <StatBlade label="H" value={oracle.H.toFixed(3)} detail="entropy" tone="danger" />
          <StatBlade label="C" value={oracle.C.toFixed(3)} detail="coherence" tone="signal" />
          <StatBlade label="S" value={oracle.S.toFixed(3)} detail="structure" tone="warning" />
        </div>
      </section>
      <section className="aeon-os-intel-stack">
        {oracle.notes.map((note) => <article key={note}>{note}</article>)}
        {oracle.blindspots.map((blindspot) => (
          <article key={`${blindspot.label}-${blindspot.engine}`} className="is-blindspot">
            <strong>{blindspot.label}</strong>
            <span>{blindspot.engine} / severity {blindspot.severity}</span>
          </article>
        ))}
      </section>
    </div>
  );
}

function RegimeTag({ regime }) {
  const colors = {
    MOMENTUM: { bg: "rgba(34,197,94,0.15)", border: "rgba(34,197,94,0.4)", text: "#86efac" },
    MEAN_REVERSION: { bg: "rgba(59,130,246,0.15)", border: "rgba(59,130,246,0.4)", text: "#93c5fd" },
    RANDOM_WALK: { bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.4)", text: "#fca5a5" },
    TRANSITIONAL: { bg: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.4)", text: "#fcd34d" },
    UNKNOWN: { bg: "rgba(100,116,139,0.15)", border: "rgba(100,116,139,0.3)", text: "#94a3b8" },
  };
  const c = colors[regime] || colors.UNKNOWN;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", padding: "2px 6px", borderRadius: 4,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text, textTransform: "uppercase",
    }}>
      {regime === "MEAN_REVERSION" ? "MEAN-REV" : regime === "RANDOM_WALK" ? "RAND-WALK" : regime}
    </span>
  );
}

function VoteTag({ vote }) {
  const colors = {
    BULLISH: { bg: "rgba(34,197,94,0.15)", border: "rgba(34,197,94,0.4)", text: "#86efac" },
    BEARISH: { bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.4)", text: "#fca5a5" },
    NEUTRAL: { bg: "rgba(100,116,139,0.15)", border: "rgba(100,116,139,0.3)", text: "#94a3b8" },
  };
  const c = colors[vote] || colors.NEUTRAL;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", padding: "3px 8px", borderRadius: 5,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
    }}>
      {vote}
    </span>
  );
}

function StrategyStatusDot({ status, wired }) {
  const color = wired ? "#86efac" : status === "backtested" ? "#93c5fd" : status === "active" ? "#86efac" : "#fcd34d";
  return <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block", marginRight: 6, flexShrink: 0 }} />;
}

function ResearcherScreen({ data }) {
  const r = data.researcher;
  const [activeSection, setActiveSection] = useState("hurst");

  const sections = [
    { id: "hurst", label: "Hurst Gate" },
    { id: "ensemble", label: "Consensus" },
    { id: "tcn", label: "TCN Vote" },
    { id: "calibration", label: "Calibration" },
    { id: "strategies", label: "Strategies" },
  ];

  return (
    <div className="aeon-os-layout" style={{ gap: 16 }}>
      {/* Header bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.2)", borderRadius: 8, padding: "6px 12px" }}>
          <FlaskConical style={{ width: 14, height: 14, color: "#c4b5fd" }} />
          <span style={{ fontSize: 11, color: "#c4b5fd", fontWeight: 600, letterSpacing: "0.1em" }}>QUANTUM LAB</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: r.gateActive ? "rgba(34,197,94,0.1)" : "rgba(100,116,139,0.1)", color: r.gateActive ? "#86efac" : "#64748b", border: `1px solid ${r.gateActive ? "rgba(34,197,94,0.3)" : "rgba(100,116,139,0.2)"}` }}>
            Gate 16 {r.gateActive ? "LIVE" : "OFF"}
          </span>
          {r.elevatedConsensus > 0 && (
            <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: "rgba(245,158,11,0.1)", color: "#fcd34d", border: "1px solid rgba(245,158,11,0.3)" }}>
              {r.elevatedConsensus} CONSENSUS
            </span>
          )}
        </div>
      </div>

      {/* Section tabs */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: 8 }}>
        {sections.map((s) => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} style={{
            padding: "4px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600,
            letterSpacing: "0.08em", background: activeSection === s.id ? "rgba(139,92,246,0.25)" : "rgba(255,255,255,0.04)",
            color: activeSection === s.id ? "#c4b5fd" : "rgba(255,255,255,0.4)",
            transition: "all 0.15s",
          }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Section: Hurst Gate */}
      {activeSection === "hurst" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {["MOMENTUM", "MEAN_REVERSION", "RANDOM_WALK", "TRANSITIONAL"].map((regime) => {
              const count = regime === "MOMENTUM" ? r.hurstSummary.momentum : regime === "MEAN_REVERSION" ? r.hurstSummary.mean_reversion : regime === "RANDOM_WALK" ? r.hurstSummary.random_walk : (r.hurstSummary.transitional || 0);
              return (
                <div key={regime} style={{ flex: 1, minWidth: 100, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: regime === "MOMENTUM" ? "#86efac" : regime === "MEAN_REVERSION" ? "#93c5fd" : "#fcd34d" }}>{count}</div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", marginTop: 2 }}>
                    {regime === "MEAN_REVERSION" ? "MEAN-REV" : regime.replace("_", " ")}
                  </div>
                </div>
              );
            })}
          </div>
          {r.hurst.length === 0 ? (
            <div style={{ textAlign: "center", color: "rgba(255,255,255,0.25)", fontSize: 12, padding: "20px 0" }}>
              Hurst cache building — gate will populate as engines scan symbols.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {r.hurst.map((s) => (
                <div key={s.symbol} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, padding: "6px 12px" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", minWidth: 90 }}>{s.symbol.replace("/USDT", "")}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.7)", fontFamily: "monospace" }}>H={s.hurst.toFixed(3)}</span>
                  <RegimeTag regime={s.regime} />
                  <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>{Math.round(s.age_seconds / 60)}m ago</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", marginTop: 4 }}>
            H &lt; 0.44 = mean-rev · H &gt; 0.56 = momentum · 0.48–0.52 = random walk (both windows blocked) · edges = transitional (passes)
          </div>
        </div>
      )}

      {/* Section: Ensemble Consensus */}
      {activeSection === "ensemble" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {r.ensemble.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0" }}>
              <TrendingUp style={{ width: 28, height: 28, color: "rgba(255,255,255,0.15)", margin: "0 auto 12px" }} />
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>No consensus events in the last 10 minutes.</div>
              <div style={{ color: "rgba(255,255,255,0.18)", fontSize: 11, marginTop: 6 }}>Consensus fires when 3+ engines agree on the same symbol and direction.</div>
            </div>
          ) : r.ensemble.map((c, i) => (
            <div key={`${c.symbol}-${c.direction}-${i}`} style={{
              background: c.elevated ? "rgba(245,158,11,0.06)" : "rgba(255,255,255,0.02)",
              border: `1px solid ${c.elevated ? "rgba(245,158,11,0.25)" : "rgba(255,255,255,0.07)"}`,
              borderRadius: 8, padding: "10px 14px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <strong style={{ fontSize: 13, color: "#e2e8f0" }}>{c.symbol.replace("/USDT", "")}</strong>
                  <VoteTag vote={c.direction.toUpperCase() === "LONG" ? "BULLISH" : "BEARISH"} />
                  {c.elevated && <span style={{ fontSize: 9, color: "#fcd34d", letterSpacing: "0.1em", fontWeight: 700 }}>ELEVATED</span>}
                </div>
                <span style={{ fontSize: 20, fontWeight: 700, color: c.elevated ? "#fcd34d" : "#94a3b8" }}>{c.count}</span>
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 6 }}>
                {c.engines.join(" · ")} · avg conf {c.avg_confidence}%
              </div>
            </div>
          ))}
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)" }}>
            Threshold: 3+ engines within 10 min → elevated (+25% position size bonus)
          </div>
        </div>
      )}

      {/* Section: TCN Vote */}
      {activeSection === "tcn" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 20, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: "0.12em", marginBottom: 12 }}>BTC/USDT — TCN NEURAL ENGINE 9</div>
            <VoteTag vote={r.tcn.vote || "NEUTRAL"} />
            <div style={{ marginTop: 16, display: "flex", justifyContent: "center", gap: 24 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{(r.tcn.confidence || 0).toFixed(1)}%</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>CONFIDENCE</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{(r.tcn.p_hat || 0.5).toFixed(3)}</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>P̂ BULLISH</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{(r.tcn.sharpe || 0).toFixed(3)}</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>S₉ SHARPE</div>
              </div>
            </div>
            <div style={{ marginTop: 14, fontSize: 10, color: r.tcn.live ? "#86efac" : "#f87171" }}>
              {r.tcn.live ? "INFERENCE LIVE" : "INFERENCE STALE — last run >2h ago"}
            </div>
          </div>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, padding: "12px 16px" }}>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", lineHeight: 1.7 }}>
              <div>TCN trained on BTC/USDT 1h only. Other symbols return NEUTRAL.</div>
              <div>Vote is used as ensemble input — TCN BULLISH agrees with LONG signals.</div>
              <div>α₉ = (2p̂−1) × S₉ still injected into |Ψ⟩ quantum state (unchanged).</div>
            </div>
          </div>
        </div>
      )}

      {/* Section: Calibration */}
      {activeSection === "calibration" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
            <span>{r.calibrationTotal} closed trades analysed</span>
            <span>Overall win rate: <strong style={{ color: "#e2e8f0" }}>{r.overallWinRate}%</strong></span>
          </div>
          {r.calibration.length === 0 ? (
            <div style={{ textAlign: "center", color: "rgba(255,255,255,0.25)", fontSize: 12, padding: "20px 0" }}>
              Calibration data building — needs closed paper trades with confidence scores.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {r.calibration.map((d) => {
                const gap = d.calibration_gap;
                const gapColor = Math.abs(gap) < 5 ? "#86efac" : gap > 0 ? "#fcd34d" : "#f87171";
                const barPct = Math.min(100, d.win_rate);
                return (
                  <div key={d.range} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, padding: "8px 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", width: 60 }}>{d.range}</span>
                      <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, margin: "0 10px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${barPct}%`, background: d.win_rate >= 55 ? "#86efac" : d.win_rate >= 45 ? "#fcd34d" : "#f87171", borderRadius: 3, transition: "width 0.3s" }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#e2e8f0", width: 42, textAlign: "right" }}>{d.win_rate}%</span>
                      <span style={{ fontSize: 10, color: gapColor, width: 52, textAlign: "right" }}>
                        {gap > 0 ? "+" : ""}{gap}pp
                      </span>
                      <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", width: 50, textAlign: "right" }}>n={d.count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", marginTop: 4 }}>
            Gap = actual win rate − midpoint of confidence range. Positive = over-confident. Negative = under-confident.
          </div>
        </div>
      )}

      {/* Section: Quantum Strategies */}
      {activeSection === "strategies" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {r.strategies.map((s) => (
            <div key={s.name} style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 7, padding: "10px 14px",
            }}>
              <StrategyStatusDot status={s.status} wired={s.wired} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <strong style={{ fontSize: 12, color: "#e2e8f0" }}>{s.name}</strong>
                  {s.wired && <span style={{ fontSize: 9, color: "#86efac", letterSpacing: "0.1em", fontWeight: 700 }}>WIRED</span>}
                </div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>{s.description}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                {s.backtest ? (
                  <div style={{ fontSize: 10, color: "#93c5fd" }}>
                    <div>SR {s.backtest.sharpe?.toFixed(2) || "—"}</div>
                    <div>{s.backtest.win_rate ? `${(s.backtest.win_rate * 100).toFixed(0)}% WR` : "—"}</div>
                  </div>
                ) : (
                  <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>{s.status}</span>
                )}
              </div>
            </div>
          ))}
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", marginTop: 6 }}>
            Green = wired into gate pipeline · Blue = backtested · Amber = pending implementation
          </div>
        </div>
      )}
    </div>
  );
}

function ControlScreen({ props }) {
  const actions = props.actions || {};
  const active = props.tradingStats?.active;
  const controls = [
    { icon: active ? Pause : Play, label: active ? "Pause Flow" : "Resume Flow", action: active ? actions.pauseTrading : actions.resumeTrading, danger: active },
    { icon: ScanLine, label: "Oracle Sweep", action: actions.runScan },
    { icon: Bell, label: "Alert Link", action: actions.toggleNotifications, active: props.notificationsEnabled },
    { icon: Phone, label: "Voice Link", action: actions.openVoice },
    { icon: RefreshCw, label: "Refresh Truth", action: actions.refresh },
  ];

  return (
    <div className="aeon-os-layout">
      <section className="aeon-os-control-matrix">
        {controls.map((control) => {
          const Icon = control.icon;
          return (
            <button key={control.label} onClick={control.action} className={`${control.danger ? "is-danger" : ""} ${control.active ? "is-active" : ""}`}>
              <Icon className="h-6 w-6" />
              <strong>{control.label}</strong>
              <span>{control.danger ? "armed live endpoint" : "operator command"}</span>
            </button>
          );
        })}
      </section>
      <section className="aeon-os-lock-stack">
        <StatBlade icon={Lock} label="Governance" value="GATED" detail="engine permissions active" tone="danger" />
        <StatBlade icon={Power} label="Mode" value={active ? "ACTIVE" : "PAUSED"} detail="execution posture" tone={active ? "live" : "warning"} />
        <a href="https://t.me/ObsidianCabalbot" target="_blank" rel="noopener noreferrer" className="aeon-os-telegram">
          <Send className="h-5 w-5" />
          <span>Telegram relay</span>
        </a>
      </section>
    </div>
  );
}

function ActiveScreen({ active, data, props, setActive }) {
  if (active === "engines") return <EnginesScreen data={data} />;
  if (active === "exposure") return <ExposureScreen data={data} />;
  if (active === "oracle") return <OracleScreen data={data} />;
  if (active === "control") return <ControlScreen props={props} />;
  if (active === "researcher") return <ResearcherScreen data={data} />;
  return <HubScreen data={data} props={props} setActive={setActive} />;
}

export default function AeonHub(props) {
  const [active, setActive] = useState("hub");
  const data = useMemo(() => buildAeonHubState(props), [props]);
  const meta = moduleMeta[active];
  const Icon = meta.icon;

  return (
    <div className="aeon-os-shell">
      <div className="aeon-os-bg-grid" />
      <div className="aeon-os-bg-scan" />
      <div className="aeon-os-light aeon-os-light-a" />
      <div className="aeon-os-light aeon-os-light-b" />

      <ModuleDock active={active} setActive={setActive} data={data} />

      <main className="aeon-os-main">
        <header className="aeon-os-header">
          <div>
            <div className="aeon-os-kicker"><span /> AEON HUB / {meta.signal}</div>
            <h1><Icon className="h-7 w-7" /> {meta.title}</h1>
            <p>{meta.brief}</p>
          </div>
          <StatusStrip props={props} data={data} />
        </header>

        <ActiveScreen active={active} data={data} props={props} setActive={setActive} />
      </main>
    </div>
  );
}
